// Демо-данные окна «Каталог каналов» (Channel Catalog) для главной Hub.
// Каталог — витрина провайдеров: мессенджеры, звонки, SMS, почта и онлайн-чат.
// Карточка = один провайдер: название, описание, логотип, бейдж «Популярный», кто предоставляет и способ подключения.
import { t } from "@/lib/i18n";

export type CatalogProvider = {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  provider: string;
  popular?: boolean;
  kind: "oauth" | "token" | "code";
  connected?: boolean;
};

// верхние табы каталога — как в настройках каналов
export const catalogTabs = [
  { id: "all", label: t("Все") },
  { id: "messengers", label: t("Мессенджеры") },
  { id: "calls", label: t("Звонки") },
  { id: "sms", label: "SMS" },
  { id: "email", label: t("Почта") },
  { id: "chat", label: t("Онлайн-чат") },
];

// заголовок группы карточек и иконка раздела
export const catalogSections: Record<string, { label: string; icon: string; hint: string }> = {
  messengers: {
    label: t("Мессенджеры"),
    icon: "ri-chat-3-line",
    hint: t("Переписка из WhatsApp, Telegram, Viber, Instagram и Facebook попадает в единые входящие."),
  },
  calls: {
    label: t("Звонки"),
    icon: "ri-phone-line",
    hint: t("Входящие и исходящие звонки с записью разговоров и карточкой клиента в ленте."),
  },
  sms: {
    label: "SMS",
    icon: "ri-message-2-line",
    hint: t("Короткие уведомления и подтверждения, когда клиент не в мессенджере."),
  },
  email: {
    label: t("Почта"),
    icon: "ri-mail-line",
    hint: t("Письма клиентов становятся диалогами с историей и вложениями."),
  },
  chat: {
    label: t("Онлайн-чат"),
    icon: "ri-customer-service-2-line",
    hint: t("Виджет на сайте: клиент пишет в чат, продавец отвечает из Hub."),
  },
};

export const catalogProviders: CatalogProvider[] = [
  /* ── звонки ── */
  {
    id: "telnyx",
    name: t("Telnyx (VoIP и SMS)"),
    description: t("Подключите Telnyx, чтобы принимать звонки и отправлять SMS клиентам прямо из Hub."),
    category: "calls",
    icon: "ri-phone-line",
    provider: "telnyx",
    kind: "token",
  },
  {
    id: "twilio-voice",
    name: "Twilio Voice",
    description: t("Виртуальные номера в 40 странах: звонки, запись разговоров и IVR-меню для отдела продаж."),
    category: "calls",
    icon: "ri-phone-fill",
    provider: "twilio",
    kind: "token",
  },
  {
    id: "mango-office",
    name: "Mango Office",
    description: t("Облачная АТС: входящая линия, запись разговоров и карточка клиента в ленте диалогов."),
    category: "calls",
    icon: "ri-headphone-line",
    provider: "mango",
    kind: "token",
  },
  {
    id: "whatsapp-via-voice",
    name: t("Звонки WhatsApp"),
    description: t("Голосовые звонки через WhatsApp Business — тот же номер, что и для переписки."),
    category: "calls",
    icon: "ri-whatsapp-line",
    provider: "meta",
    kind: "oauth",
  },

  /* ── мессенджеры ── */
  {
    id: "whatsapp-api",
    name: "WhatsApp Business Platform (API)",
    description: t("Подключите WhatsApp Business API через Facebook, чтобы вести переписку без телефона и лимитов на устройства."),
    category: "messengers",
    icon: "ri-whatsapp-line",
    provider: "meta",
    popular: true,
    kind: "oauth",
    connected: true,
  },
  {
    id: "facebook-messenger",
    name: "Facebook Messenger",
    description: t("Подключите Facebook Messenger и общайтесь с клиентами со страницы компании в Hub."),
    category: "messengers",
    icon: "ri-messenger-line",
    provider: "meta",
    popular: true,
    kind: "oauth",
  },
  {
    id: "telegram-api",
    name: "Telegram Bot API",
    description: t("Бот или аккаунт Telegram: диалоги приходят с ником клиента, историей и вложениями."),
    category: "messengers",
    icon: "ri-telegram-line",
    provider: "telegram",
    kind: "token",
  },
  {
    id: "instagram-direct",
    name: "Instagram Direct",
    description: t("Личные сообщения и ответы на сторис прямо в общей ленте диалогов отдела продаж."),
    category: "messengers",
    icon: "ri-instagram-line",
    provider: "meta",
    kind: "oauth",
  },
  {
    id: "viber-business",
    name: "Viber Business Messages",
    description: t("Официальный аккаунт с рассылками и поддержкой клиентов — с именем отправителя вместо номера."),
    category: "messengers",
    icon: "ri-phone-line",
    provider: "rakuten",
    kind: "token",
  },
  {
    id: "avito-messages",
    name: t("Avito Сообщения"),
    description: t("Сообщения по объявлениям и заявки с Avito попадают в Hub рядом с WhatsApp и Telegram."),
    category: "messengers",
    icon: "ri-store-2-line",
    provider: "avito",
    kind: "oauth",
  },

  /* ── sms ── */
  {
    id: "twilio-sms",
    name: "Twilio Programmable SMS",
    description: t("Массовые и сервисные SMS с подтверждением доставки и коротким именем отправителя."),
    category: "sms",
    icon: "ri-message-2-line",
    provider: "twilio",
    kind: "token",
  },
  {
    id: "sms-ru",
    name: "SMS.RU",
    description: t("Отправка SMS по России: имя отправителя, отчёты о доставке и шаблоны уведомлений."),
    category: "sms",
    icon: "ri-smartphone-line",
    provider: "sms.ru",
    kind: "token",
  },

  /* ── почта ── */
  {
    id: "gmail-workspace",
    name: "Gmail / Google Workspace",
    description: t("Ящик Google становится каналом: письма клиентов открываются как диалоги с историей."),
    category: "email",
    icon: "ri-google-line",
    provider: "google",
    popular: true,
    kind: "oauth",
  },
  {
    id: "imap-smtp",
    name: t("Своя почта (IMAP / SMTP)"),
    description: t("Яндекс, Mail.ru или собственный сервер: подключите по адресу, логину и паролю приложения."),
    category: "email",
    icon: "ri-mail-line",
    provider: "imap",
    kind: "token",
  },
  {
    id: "outlook",
    name: "Microsoft Outlook",
    description: t("Корпоративная почта Outlook с календарём встреч и вложениями в карточке клиента."),
    category: "email",
    icon: "ri-microsoft-line",
    provider: "microsoft",
    kind: "oauth",
  },

  /* ── онлайн-чат ── */
  {
    id: "website-widget",
    name: t("Чат Hub на сайте"),
    description: t("Виджет с WhatsApp, Telegram и почтой: клиент выбирает канал, чат идёт во входящие."),
    category: "chat",
    icon: "ri-customer-service-2-line",
    provider: "hub",
    popular: true,
    kind: "code",
  },
  {
    id: "jivo",
    name: "Jivo",
    description: t("Подключите Jivo, чтобы переписка с сайта шла в Hub вместе с остальными каналами."),
    category: "chat",
    icon: "ri-chat-smile-2-line",
    provider: "jivo",
    kind: "token",
  },
  {
    id: "crisp",
    name: "Crisp",
    description: t("Мультиязычный чат для сайта: история, файлы и передача диалога продавцу в один клик."),
    category: "chat",
    icon: "ri-chat-4-line",
    provider: "crisp",
    kind: "token",
  },
];

// способы подключения — подпись в окне подключения
export const catalogKinds: Record<"oauth" | "token" | "code", { label: string; hint: string }> = {
  oauth: {
    label: t("Авторизация в сервисе"),
    hint: t("Откроем окно авторизации. Hub запросит только доступ к сообщениям и имени профиля — публиковать ничего не сможем."),
  },
  token: {
    label: t("Подключение по ключу"),
    hint: t("Скопируйте ключ в кабинете провайдера. Hub хранит его в защищённом виде и показывает только администратору."),
  },
  code: {
    label: t("Код для сайта"),
    hint: t("Вставьте код виджета перед закрывающим тегом </body> — и чат появится на всех страницах сайта."),
  },
};